import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory, Switch, Route, useRouteMatch, useParams, Redirect } from 'react-router-dom';
import { getMentorMatchFromProfile } from '../../../../api/profiles.js';
import { findAndSetMentor } from '../../../../helpers/find_mentor_helper.js';
import { updateMatchAndSet } from '../../../../helpers/mentor_match_helper.js';

import Context from '../../../../utils/context.js';
import Age from '../../question_components/age/index.jsx';
import PatientGender from '../../question_components/patient_gender.jsx';
import OptionsAndOther from '../../question_components/options_and_other.jsx';
import QuestionnaireStopper from '../../question_components/questionnaire_stopper';
import LoadingScreen from '../../../system/loading_screen/index.jsx';
import MentorMatch from '../../question_components/mentor_match/index.jsx';
import ImportantSafetyInformationStickyFooter from '../../../isi/sticky_footer/index.jsx';
import BylvayIsi from '../../../isi/important_safety_information/bylvay/index.jsx';

import styles from '../../questionnaire.module.css';

const ALGS_MIN_AGE_MONTHS = 12;
const PFIC_MIN_AGE_MONTHS = 3;
const ADULT_MIN_AGE_MONTHS = 18 * 12;

const PATIENT_OR_CAREGIVER_OPTIONS = {
  PATIENT_PFIC: 'A patient with PFIC',
  PATIENT_ALGS: 'A patient with ALGS',
  CAREGIVER_PFIC: 'A caregiver of someone with PFIC',
  CAREGIVER_ALGS: 'A caregiver of someone with ALGS',
};

const isCaregiverChoice = (choice) =>
  choice === PATIENT_OR_CAREGIVER_OPTIONS.CAREGIVER_PFIC || choice === PATIENT_OR_CAREGIVER_OPTIONS.CAREGIVER_ALGS;

const conditionFromPatientOrCaregiver = (choice) => {
  if (!choice) return null;
  if (choice.includes('PFIC')) return 'PFIC';
  if (choice.includes('ALGS')) return 'ALGS';
  return null;
};

// Returns the patient's age in whole months relative to `today` (defaults to "now"). The
// component-level age check uses this to decide whether the patient qualifies for the program.
const ageInMonths = (dateOfBirth, today = new Date()) => {
  const dob = new Date(dateOfBirth);
  let months = (today.getFullYear() - dob.getFullYear()) * 12 + (today.getMonth() - dob.getMonth());
  if (today.getDate() < dob.getDate()) months -= 1;
  return months;
};

// BYLVAY is indicated for patients 12+ months with ALGS and 3+ months with PFIC. Patients outside
// those ranges (or with an unknown condition) are not eligible for the mentor program.
const isPatientAgeEligible = (dateOfBirth, condition) => {
  if (!dateOfBirth || !condition) return false;
  const months = ageInMonths(dateOfBirth);
  if (condition === 'ALGS') return months >= ALGS_MIN_AGE_MONTHS;
  if (condition === 'PFIC') return months >= PFIC_MIN_AGE_MONTHS;
  return false;
};

// The mentor program itself is restricted to adults (18+), so a self-identified patient who is a
// minor is not eligible to participate even if BYLVAY is medically indicated for their age.
const isAdult = (dateOfBirth) => {
  if (!dateOfBirth) return false;
  return ageInMonths(dateOfBirth) >= ADULT_MIN_AGE_MONTHS;
};

export default function BylvayQuestionnaire({}) {
  const context = useContext(Context);
  const history = useHistory();
  const { id } = useParams();
  const [patientOrCaregiver, setPatientOrCaregiver] = useState(
    () => sessionStorage.getItem('bylvayPatientOrCaregiver') || undefined
  );
  const [caringFor, setCaringFor] = useState();
  const [currentBirthday, setCurrentBirthday] = useState(
    context.auth.isLoggedIn() ? context.auth.getCurrentUser().date_of_birth : null
  );
  const [patientDateOfBirth, setPatientDateOfBirth] = useState();
  const [gender, setGender] = useState();
  const [hasHadTreatment, setHasHadTreatment] = useState();
  const [symptomManagement, setSymptomManagement] = useState();
  const [consideringASwitch, setConsideringASwitch] = useState();
  const [hasSpokenWithDoctorAboutTreatment, setHasSpokenWithDoctorAboutTreatment] = useState();
  const [scheduledTreatment, setScheduledTreatment] = useState();
  const [mentorConversationTopics, setMentorConversationTopics] = useState(() => {
    const saved = sessionStorage.getItem('mentorConversationTopics');
    return saved ? JSON.parse(saved) : [];
  });
  const [noMedicalAdviceDisclaimer, setNoMedicalAdviceDisclaimer] = useState();
  const [monitoringAndSafetyReporting, setMonitoringAndSafetyReporting] = useState();
  const [mentorMatch, setMentorMatch] = useState(() => {
    const saved = sessionStorage.getItem('mentorMatch');
    return saved ? JSON.parse(saved) : null;
  });
  const [patientPartner, setPatientPartner] = useState(() => {
    const saved = sessionStorage.getItem('patientPartner');
    return saved ? JSON.parse(saved) : '';
  });
  const [mentorFound, setMentorFound] = useState(false);
  const isiRef = useRef(null);

  let { path } = useRouteMatch();
  path = path.replace(`:id`, id);

  const update = (params, callback) => {
    updateMatchAndSet({ uuid: mentorMatch?.uuid, id, params, setMentorMatch, callback });
  };

  useEffect(() => {
    if (mentorFound && patientPartner) {
      history.push(`${path}/patient-partner`);
    }
  }, [mentorFound, patientPartner]);

  useEffect(() => {
    if (context.auth.isLoggedIn()) {
      getMentorMatchFromProfile((match) => {
        sessionStorage.setItem('mentorMatch', JSON.stringify(match));
        setMentorMatch(match);
        if (history.location.pathname === path) {
          history.push(`/member/mentor-matches/${match.uuid}/bylvay/patient-or-caregiver`);
        }
      });
    }
  }, []);

  return (
    <div className={styles.questionnaire_ottr} id='questionnaire'>
      <div className={`${styles.questionnaire_content}`}>
        <div className={`${styles.cont_row}`}>
          <Switch>
            <Route exact path={`${path}`}>
              <Redirect to={`${path}/patient-or-caregiver`} />
            </Route>

            {/* 1 */}

            <Route exact path={`${path}/patient-or-caregiver`}>
              <OptionsAndOther
                title={
                  'This program is designed for patients or caregivers affected by progressive familial intrahepatic cholestasis (PFIC) or Alagille syndrome (ALGS). Which best describes you?'
                }
                answers={{ patient_or_caregiver: patientOrCaregiver }}
                keyForAnswer={'patient_or_caregiver'}
                choices={[
                  'A patient with PFIC',
                  'A patient with ALGS',
                  'A caregiver of someone with PFIC',
                  'A caregiver of someone with ALGS',
                  'None of the above',
                ]}
                showOther={false}
                selectOne
                onSubmit={(answers) => {
                  const answer = answers.patient_or_caregiver;
                  const condition = answer.includes('PFIC') ? 'PFIC' : answer.includes('ALGS') ? 'ALGS' : null;
                  const treatment = condition;
                  const lowerAnswer = answer.toLowerCase();
                  const patient_or_caregiver = lowerAnswer.includes('caregiver')
                    ? 'Caregiver'
                    : lowerAnswer.includes('patient')
                      ? 'Patient'
                      : null;
                  setPatientOrCaregiver(answer);
                  sessionStorage.setItem('bylvayPatientOrCaregiver', answer);
                  update(
                    {
                      treatment: treatment,
                      condition: condition,
                      data: { patient_or_caregiver: patient_or_caregiver },
                    },
                    () => {
                      if (answer === 'None of the above') {
                        history.push(`${path}/thank-you`);
                      } else if (
                        answer === 'A caregiver of someone with PFIC' ||
                        answer === 'A caregiver of someone with ALGS'
                      ) {
                        history.push(`${path}/caring-for`);
                      } else {
                        history.push(`${path}/age`);
                      }
                    }
                  );
                }}
              />
            </Route>

            {/* 2 */}

            <Route exact path={`${path}/caring-for`}>
              <OptionsAndOther
                title={'Who are you caring for?'}
                answers={{ caring_for: caringFor }}
                keyForAnswer={'caring_for'}
                choices={['A child', 'An adult family member']}
                showOther={true}
                otherTxt={'Other (specify)'}
                selectOne
                onSubmit={(answers) => {
                  const answer = answers.caring_for || answers['other'];
                  setCaringFor(answer);
                  update({ data: { caring_for: answer } }, () => {
                    history.push(`${path}/age`);
                  });
                }}
                onBack={() => {
                  history.push(`${path}/patient-or-caregiver`);
                }}
              />
            </Route>

            {/* 3 */}

            <Route exact path={`${path}/age`}>
              <Age
                key='age'
                title='What is your date of birth?'
                para=''
                answers={{ date_of_birth: currentBirthday }}
                onSubmit={(answers) => {
                  setCurrentBirthday(answers.date_of_birth);
                  context.auth.userUpdate({ date_of_birth: answers.date_of_birth }, () => {
                    if (!isAdult(answers.date_of_birth)) {
                      history.push(`${path}/thank-you`);
                      return;
                    }

                    if (isCaregiverChoice(patientOrCaregiver)) {
                      history.push(`${path}/patient-age`);
                      return;
                    }

                    const condition = conditionFromPatientOrCaregiver(patientOrCaregiver);
                    if (isPatientAgeEligible(answers.date_of_birth, condition)) {
                      history.push(`${path}/gender`);
                    } else {
                      history.push(`${path}/thank-you`);
                    }
                  });
                }}
                onBack={() => {
                  history.push(
                    isCaregiverChoice(patientOrCaregiver) ? `${path}/caring-for` : `${path}/patient-or-caregiver`
                  );
                }}
              />
            </Route>

            {/* 4 */}

            <Route exact path={`${path}/patient-age`}>
              <Age
                key='patient-age'
                title="If you are a caregiver, what is the progressive familial intrahepatic cholestasis (PFIC) or Alagille syndrome (ALGS) patient's date of birth?"
                para=''
                answers={{ date_of_birth: patientDateOfBirth }}
                onSubmit={(answers) => {
                  setPatientDateOfBirth(answers.date_of_birth);
                  update({ patient_dob: answers.date_of_birth }, () => {
                    const condition = conditionFromPatientOrCaregiver(patientOrCaregiver);
                    if (isPatientAgeEligible(answers.date_of_birth, condition)) {
                      history.push(`${path}/gender`);
                    } else {
                      history.push(`${path}/thank-you`);
                    }
                  });
                }}
                onBack={() => {
                  history.push(`${path}/age`);
                }}
              />
            </Route>

            {/* 5 */}

            <Route exact path={`${path}/gender`}>
              <PatientGender
                answers={{ gender: gender }}
                title='How would you describe your gender?'
                para=''
                choices={['Male', 'Female', 'Non-binary', 'Prefer not to answer']}
                onSubmit={(answers) => {
                  setGender(answers.gender);
                  context.auth.userUpdate({ gender: answers.gender }, () => {
                    history.push(`${path}/prescription`);
                  });
                }}
                onBack={() => {
                  history.push(isCaregiverChoice(patientOrCaregiver) ? `${path}/patient-age` : `${path}/age`);
                }}
              />
            </Route>

            {/* 6 */}

            <Route exact path={`${path}/prescription`}>
              <OptionsAndOther
                title={'Are you (or someone you care for) currently taking BYLVAY (odevixibat)?'}
                answers={{ has_had_treatment: hasHadTreatment }}
                keyForAnswer={'has_had_treatment'}
                choices={['Yes', 'No', 'Not sure']}
                showOther={false}
                selectOne
                onSubmit={(answers) => {
                  const answer = answers['has_had_treatment'];
                  setHasHadTreatment(answer);
                  update({ data: { has_had_treatment: answers.has_had_treatment } }, () => {
                    if (answer === 'Yes') {
                      history.push(`${path}/symptom-management`);
                    } else {
                      history.push(`${path}/considering-a-switch`);
                    }
                  });
                }}
                onBack={() => {
                  history.push(`${path}/gender`);
                }}
              />

              <ImportantSafetyInformationStickyFooter
                component={<BylvayIsi showFooter />}
                initialHeight={350}
                initialHeightMobile={200}
                expandedHeight={800}
                ref={isiRef}
              />
            </Route>

            {/* 7 */}

            <Route exact path={`${path}/symptom-management`}>
              <OptionsAndOther
                title={'Please specify how long you (or someone you care for) have been taking BYLVAY (odevixibat).'}
                answers={{ symptom_management: symptomManagement }}
                keyForAnswer={'symptom_management'}
                choices={['Less than 1 month', '1-3 months', '4-6 months', '7 months to a year', 'More than 1 year']}
                showOther={false}
                selectOne
                onSubmit={(answers) => {
                  setSymptomManagement(answers['symptom_management']);
                  update({ data: { symptom_management: answers.symptom_management } }, () => {
                    history.push(`${path}/mentor-conversation-topics`);
                  });
                }}
                onBack={() => {
                  history.push(`${path}/prescription`);
                }}
              />

              <ImportantSafetyInformationStickyFooter
                initialHeight={300}
                initialHeightMobile={200}
                expandedHeight={800}
                component={<BylvayIsi />}
                ref={isiRef}
              />
            </Route>

            {/* 8 */}

            <Route exact path={`${path}/considering-a-switch`}>
              <OptionsAndOther
                title={
                  'Are you (or someone you care for) considering starting BYLVAY (odevixibat) for itch associated with PFIC or ALGS?'
                }
                answers={{ considering_a_switch: consideringASwitch }}
                keyForAnswer={'considering_a_switch'}
                choices={['Yes', 'No', 'Maybe']}
                showOther={false}
                selectOne
                onSubmit={(answers) => {
                  setConsideringASwitch(answers['considering_a_switch']);
                  update({ data: { considering_a_switch: answers.considering_a_switch } }, () => {
                    history.push(`${path}/has-spoken-with-doctor-about-treatment`);
                  });
                }}
                onBack={() => {
                  history.push(`${path}/prescription`);
                }}
              />

              <ImportantSafetyInformationStickyFooter
                initialHeight={350}
                initialHeightMobile={200}
                expandedHeight={800}
                component={<BylvayIsi />}
                ref={isiRef}
              />
            </Route>

            {/* 9 */}

            <Route exact path={`${path}/has-spoken-with-doctor-about-treatment`}>
              <OptionsAndOther
                title={'Have you spoken with your doctor about BYLVAY (odevixibat)?'}
                answers={{ has_spoken_with_doctor_about_treatment: hasSpokenWithDoctorAboutTreatment }}
                keyForAnswer={'has_spoken_with_doctor_about_treatment'}
                choices={['Yes', 'No']}
                showOther={false}
                selectOne
                onSubmit={(answers) => {
                  setHasSpokenWithDoctorAboutTreatment(answers['has_spoken_with_doctor_about_treatment']);
                  update(
                    {
                      data: {
                        has_spoken_with_doctor_about_treatment: answers.has_spoken_with_doctor_about_treatment,
                      },
                    },
                    () => {
                      history.push(`${path}/current-prescription`);
                    }
                  );
                }}
                onBack={() => {
                  history.push(`${path}/considering-a-switch`);
                }}
              />

              <ImportantSafetyInformationStickyFooter
                initialHeight={350}
                initialHeightMobile={200}
                expandedHeight={800}
                component={<BylvayIsi />}
                ref={isiRef}
              />
            </Route>

            {/* 10 */}

            <Route exact path={`${path}/current-prescription`}>
              <OptionsAndOther
                title={'Do you have a prescription for BYLVAY (odevixibat)?'}
                answers={{ scheduled_treatment: scheduledTreatment }}
                keyForAnswer={'scheduled_treatment'}
                choices={['Yes', 'No']}
                showOther={false}
                selectOne
                onSubmit={(answers) => {
                  setScheduledTreatment(answers['scheduled_treatment']);
                  update({ data: { scheduled_treatment: answers.scheduled_treatment } }, () => {
                    history.push(`${path}/mentor-conversation-topics`);
                  });
                }}
                onBack={() => {
                  history.push(`${path}/has-spoken-with-doctor-about-treatment`);
                }}
              />

              <ImportantSafetyInformationStickyFooter
                initialHeight={350}
                initialHeightMobile={200}
                expandedHeight={800}
                component={<BylvayIsi />}
                ref={isiRef}
              />
            </Route>

            {/* 11 */}

            <Route exact path={`${path}/mentor-conversation-topics`}>
              <OptionsAndOther
                title={'What are you hoping to get out of connecting with a mentor? (Select all that apply)'}
                para={
                  'Mentors share personal experiences only and cannot provide medical advice or treatment guidance.'
                }
                answers={{ mentor_conversation_topics: mentorConversationTopics }}
                keyForAnswer={'mentor_conversation_topics'}
                choices={[
                  "Hearing someone's personal experience living with PFIC or ALGS",
                  'Understanding what day-to-day life looks like for others',
                  'Feeling less alone',
                  'Caregiver-to-caregiver perspective',
                  'Learning how others approached conversations with their doctor',
                  'Tips that helped others stay organized or navigate daily routines',
                ]}
                selectOne={false}
                showOther={true}
                otherTxt={"Other topics you'd like to discuss (optional)"}
                onSubmit={(answers) => {
                  const other = answers['other'];
                  const result = [...(answers.mentor_conversation_topics || []), ...(other ? [other] : [])];
                  sessionStorage.setItem('mentorConversationTopics', JSON.stringify(result));
                  setMentorConversationTopics(result);
                  update({ conversation_topics: result }, () => {
                    history.push(`${path}/no-medical-advice-disclaimer`);
                  });
                }}
                onBack={() => {
                  if (hasHadTreatment === 'Yes') {
                    history.push(`${path}/symptom-management`);
                  } else {
                    history.push(`${path}/current-prescription`);
                  }
                }}
              />
            </Route>

            {/* 12 */}

            <Route exact path={`${path}/no-medical-advice-disclaimer`}>
              <OptionsAndOther
                title={
                  'Mentors are not medical professionals. They cannot provide medical advice or discuss specific side effects. I understand that conversations are personal experience only.'
                }
                answers={{ no_medical_advice_disclaimer: noMedicalAdviceDisclaimer }}
                keyForAnswer={'no_medical_advice_disclaimer'}
                choices={['Yes, I understand']}
                showOther={false}
                selectOne
                onSubmit={(answers) => {
                  setNoMedicalAdviceDisclaimer(answers['no_medical_advice_disclaimer']);
                  update(
                    {
                      data: { no_medical_advice_disclaimer: 'Yes', no_medical_advice_disclaimer_datetime: new Date() },
                    },
                    () => {
                      history.push(`${path}/monitoring-and-safety-reporting`);
                    }
                  );
                }}
                onBack={() => {
                  history.push(`${path}/mentor-conversation-topics`);
                }}
              />
            </Route>

            {/* 13 */}

            <Route exact path={`${path}/monitoring-and-safety-reporting`}>
              <OptionsAndOther
                title={
                  <>
                    <div>
                      PatientPartner conducts limited monitoring of all conversations to help ensure patient well-being.
                      This review does not reveal your identity and is handled with strict privacy safeguards.
                    </div>
                    <br></br>
                    <div>
                      PatientPartner is responsible for identifying and reporting on side effects as they learn of them
                      to share with Ipsen. All of the information is kept confidential. The information collected is
                      used by Ipsen to continue to monitor the safety of Ipsen's marketed products.
                    </div>
                    <br></br>
                    <div>I acknowledge and understand this.</div>
                  </>
                }
                answers={{ monitoring_and_safety_reporting: monitoringAndSafetyReporting }}
                keyForAnswer={'monitoring_and_safety_reporting'}
                choices={['Yes, I understand']}
                showOther={false}
                selectOne
                onSubmit={(answers) => {
                  setMonitoringAndSafetyReporting(answers.monitoring_and_safety_reporting);
                  update(
                    {
                      data: {
                        monitoring_and_safety_reporting: 'Yes',
                        monitoring_and_safety_reporting_datetime: new Date(),
                      },
                    },
                    () => {
                      history.push(`${path}/loading-patient-partner`);
                    }
                  );
                  findAndSetMentor(
                    {
                      onSetPatientPartner: setPatientPartner,
                      onMentorFound: setMentorFound,
                      onNoMentorFound: () => history.push(`${path}/no-mentor-available`),
                    },
                    context
                  );
                }}
                onBack={() => {
                  history.push(`${path}/no-medical-advice-disclaimer`);
                }}
              />
            </Route>

            {/* 14 */}

            <Route exact path={`${path}/thank-you`}>
              <QuestionnaireStopper
                wide
                header='Thank you for your interest in the BYLVAY (odevixibat) Mentor Program.'
                para={
                  <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#232323' }}>
                    <p>
                      We understand how important it can be to connect with others who have shared experiences. At this
                      time, the BYLVAY Mentor Program is available{' '}
                      <strong>only to adults (18 years of age and older)</strong> who are patients or caregivers
                      affected by itch associated with progressive familial intrahepatic cholestasis (PFIC) or Alagille
                      syndrome (ALGS) and who meet the program's eligibility criteria.
                    </p>
                    <br />
                    <p>BYLVAY (odevixibat) is a prescription medicine for the treatment of itching in:</p>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '6px 0' }}>
                      <li>Patients 12 months of age and older with Alagille syndrome (ALGS)</li>
                      <li>
                        Patients 3 months of age and older with progressive familial intrahepatic cholestasis (PFIC)
                      </li>
                    </ul>
                    <br />
                    <p>
                      <strong>Limitation of Use:</strong>
                      <br />
                      BYLVAY is not recommended for some PFIC type 2 patients who have specific <em>ABCB11</em> gene
                      changes. Talk with your healthcare provider if you have questions.
                    </p>
                    <br />
                    <p>
                      Participation in this program is limited to adults. Based on your responses, you are not eligible
                      to participate in this program at this time.
                    </p>
                    <br />
                    <p>
                      If you have any questions or would like more information, please reach out to us at{' '}
                      <strong>
                        <a
                          href='mailto:info@patientpartner.com'
                          style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                        >
                          info@patientpartner.com
                        </a>
                      </strong>
                      . You may also wish to speak with your healthcare provider or visit{' '}
                      <strong>
                        <a
                          href='https://www.bylvay.com'
                          target='_blank'
                          rel='noreferrer'
                          style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                        >
                          BYLVAY.com
                        </a>
                      </strong>{' '}
                      for additional information.
                    </p>
                    <br />
                    <p style={{ color: '#314d69', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      IMPORTANT SAFETY INFORMATION
                    </p>
                    <br />
                    <p style={{ color: '#314d69' }}>
                      <strong>What are the possible side effects of BYLVAY?</strong>
                      <br />
                      <strong>BYLVAY can cause serious side effects, including:</strong>
                    </p>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '6px 0' }}>
                      <li>
                        You should not use BYLVAY if you have had or have an advanced stage of liver disease. Talk with
                        your healthcare provider if you have any liver conditions.
                      </li>
                      <li>
                        Abnormal liver tests have been observed with use of BYLVAY. Liver tests should be obtained
                        before starting and periodically during therapy to monitor for liver injury. Immediately report
                        to your healthcare provider any signs or symptoms of liver injury, such as, nausea, vomiting,
                        skin or the whites of eyes turn yellow, dark or brown urine, pain on the right side of the
                        abdomen, or loss of appetite.
                      </li>
                      <li>Notify your healthcare provider if you experience new onset or worsening of diarrhea.</li>
                      <li>
                        BYLVAY may decrease the absorption of certain vitamins, which include vitamins A, D, E, and K
                        (FSV or fat-soluble vitamins). Your blood levels of these vitamins should be measured before
                        starting and periodically during treatment to assess for FSV deficiency. You may bleed more
                        easily, may bleed longer, or experience a bone fracture. Call your healthcare provider if you
                        have any signs or symptoms of bleeding.
                      </li>
                    </ul>
                    <br />
                    <p>
                      <strong>The most common side effects of BYLVAY include:</strong>
                    </p>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '6px 0' }}>
                      <li>
                        The most common side effects of BYLVAY treatment in patients with ALGS are diarrhea, abdominal
                        pain, abnormal bleeding, such as bruising, and decreased weight.
                      </li>
                      <li>
                        The most common side effects of BYLVAY in patients with PFIC are diarrhea, liver test
                        abnormalities, vomiting, abdominal pain, and FSV deficiency.
                      </li>
                      <li>
                        Do not swallow the 200 mcg or 600 mcg capsule(s) containing Oral Pellets whole. These are
                        intended to be opened and the contents mixed into soft food.
                      </li>
                      <li>
                        For patients taking bile acid binding resins, take BYLVAY at least 4 hours before or 4 hours
                        after taking a bile acid binding resin.
                      </li>
                      <li>
                        There is a pregnancy safety study that collects outcome data in women taking BYLVAY during
                        pregnancy. Pregnant women exposed to BYLVAY should report if they have used BYLVAY during
                        pregnancy by calling 1-855-463-5127 and contact your doctor immediately.
                      </li>
                    </ul>
                    <br />
                    <p>
                      <strong>
                        You are encouraged to report side effects to FDA at{' '}
                        <a
                          href='tel:18003321088'
                          style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                        >
                          1-800-FDA-1088
                        </a>{' '}
                        or at{' '}
                        <a
                          href='https://www.fda.gov/medwatch'
                          target='_blank'
                          rel='noreferrer'
                          style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                        >
                          www.fda.gov/medwatch
                        </a>
                        . You may also report side effects to Ipsen Biopharmaceuticals, Inc. at 1-855-463-5127.
                      </strong>
                    </p>
                    <br />
                    <p>
                      <strong>
                        Please see full{' '}
                        <a
                          href='https://d2rkmuse97gwnh.cloudfront.net/a88aa6d6-3ca0-4362-a711-d53c45ae33ff/5f7d23c0-f85b-4f9a-907a-3f2fa18aa260/5f7d23c0-f85b-4f9a-907a-3f2fa18aa260_source__v.pdf'
                          target='_blank'
                          rel='noreferrer'
                          style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                        >
                          Prescribing Information
                        </a>{' '}
                        and{' '}
                        <a
                          href='https://d2rkmuse97gwnh.cloudfront.net/a88aa6d6-3ca0-4362-a711-d53c45ae33ff/654d0431-ec8d-45f0-ba80-8a33c9df68ab/654d0431-ec8d-45f0-ba80-8a33c9df68ab_source__v.pdf'
                          target='_blank'
                          rel='noreferrer'
                          style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                        >
                          Instructions for Use
                        </a>{' '}
                        for BYLVAY.
                      </strong>
                    </p>
                  </div>
                }
                onBack={() => {}}
              />
            </Route>

            {/* 15 */}

            <Route exact path={`${path}/no-mentor-available`}>
              <QuestionnaireStopper
                wide
                header='Thank you for your interest in the BYLVAY (odevixibat) Mentor Program.'
                para={
                  <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#232323' }}>
                    <p>
                      We appreciate you taking the time to share your experience. Unfortunately, we don't currently have
                      a mentor available who matches your specific journey — but we're actively working to expand our
                      mentor community.
                    </p>
                    <br />
                    <p>
                      We'll keep your information on file and reach out as soon as a mentor who shares your experience
                      becomes available. In the meantime, if you have questions or would like more information, please
                      reach out to us at{' '}
                      <strong>
                        <a
                          href='mailto:support@patientpartner.com'
                          style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                        >
                          support@patientpartner.com
                        </a>
                      </strong>
                      . You may also wish to speak with your healthcare provider or visit{' '}
                      <strong>
                        <a
                          href='https://www.bylvay.com'
                          target='_blank'
                          rel='noreferrer'
                          style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                        >
                          BYLVAY.com
                        </a>
                      </strong>{' '}
                      for additional information.
                    </p>
                  </div>
                }
                onBack={() => {}}
              />
            </Route>

            <Route exact path={`${path}/loading-patient-partner`}>
              <LoadingScreen
                title={'Finding your mentor...'}
                content={
                  'Get ready to meet a real patient who will answer your questions and support you through your journey!'
                }
              />
            </Route>

            <Route exact path={`${path}/patient-partner`}>
              <MentorMatch
                patientPartner={patientPartner}
                stateOnlyLocation
                callback={(conversation) => {
                  updateMatchAndSet({
                    uuid: mentorMatch.uuid,
                    params: { conversation_id: conversation.id },
                    setMentorMatch,
                    callback: () => {
                      sessionStorage.removeItem('mentorConversationTopics');
                      sessionStorage.removeItem('mentorMatch');
                      sessionStorage.removeItem('patientPartner');
                      sessionStorage.removeItem('bylvayPatientOrCaregiver');
                      history.push(`/member/conversations/${conversation.uuid}`);
                    },
                  });
                }}
                whatsNextMessage={(p) => {
                  return `Send your first message to ${p.name} and get answers to questions about their experience.`;
                }}
              />
            </Route>
          </Switch>
        </div>
      </div>
    </div>
  );
}
